import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

import { useDebounce } from "../hooks/useDebounce";

const BasicForm = (props) => {
  const { user, handleBasicFormSubmit, setIsNewUser } = props;
  const [email, setEmail] = useState(user.email);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [checkingEmailAvailable, setCheckingEmailAvailable] = useState(false);
  const debouncedValue = useDebounce(email, 500);

  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Este campo es requerido"),
    email: Yup.string()
      .email("Correo electrónico inválido")
      .required("Este campo es requerido"),
  });

  useEffect(() => {
    if (debouncedValue) {
      setCheckingEmailAvailable(true);
      validateEmailAvailability();
    }

    if (debouncedValue.length == 0) {
      setEmailAvailable(null);
    }
  }, [debouncedValue]);

  const handleOnEmailChange = (evt) => {
    setEmail(evt.target.value);
  };

  const validateEmailAvailability = useCallback(async () => {
    const abortController = new AbortController();

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({
        klass: "User",
        attribute: "email",
        value: debouncedValue,
      }),
      signal: abortController.signal,
    };

    const res = await fetch("/api/v1/field_validations", options);
    const json = await res.json();

    if (json.valid) {
      setEmailAvailable(true);
      setCheckingEmailAvailable(false);
      setIsNewUser(true);
    } else if (json.message == "ya ha sido tomado") {
      setEmailAvailable(false);
      setCheckingEmailAvailable(false);
      setIsNewUser(false);
    }
  }, [debouncedValue]);

  return (
    <div className="pb-3">
      <h2 className="font-bold text-white text-3xl">Unete a la Open Week 24</h2>
      <p className="text-md text-gray-500">
        Registra una cuenta y verifica tu número de teléfono para acceder a la
        Open Week
      </p>

      <Formik
        initialValues={{
          full_name: user.full_name,
          email: user.email,
        }}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleBasicFormSubmit(values);
        }}
      >
        {({ values, setFieldValue, errors, touched, setTouched, isValid }) => (
          <Form className="space-y-6 mt-4">
            <div>
              <Field
                name="full_name"
                placeholder="Nombre completo"
                className={`appearance-none bg-gray-900 block w-full px-3 py-2 border-2 text-gray-300 border-gray-900 rounded-md placeholder-gray-600 focus:outline-none focus:shadow-outline-blue focus:border-gray-600 transition duration-150 ease-in-out sm:text-sm sm:leading-5 ${
                  touched.full_name && errors.full_name
                    ? "focus:border-red-400 border-red-400 placeholder-red-400"
                    : ""
                }`}
              />
              {touched.full_name && errors.full_name && (
                <span className="text-xs text-red-400 mt-1">
                  {errors.full_name}
                </span>
              )}
            </div>

            <div>
              <div className="relative">
                <Field
                  name="email"
                  type="email"
                  placeholder="Correo electrónico"
                  value={email}
                  onChange={(evt) => {
                    handleOnEmailChange(evt);
                    setFieldValue("email", evt.target.value);
                  }}
                  className={`appearance-none block w-full px-3 py-2 bg-gray-900 text-gray-300 border-2 border-gray-900 rounded-md placeholder-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-gray-600 transition duration-150 ease-in-out sm:text-sm sm:leading-5 ${
                    touched.email && errors.email
                      ? "border-red-400 focus:border-red-400 placeholder-red-400"
                      : ""
                  }`}
                />
                <div>
                  {checkingEmailAvailable ? (
                    <Loader2 className="absolute right-0 top-0 mt-3 mr-3 w-4 h-4 text-gray-500 animate-spin" />
                  ) : null}

                  {emailAvailable !== null && emailAvailable ? (
                    <CheckCircle2 className="absolute right-0 top-0 mt-3 mr-3 w-4 h-4 text-green-600" />
                  ) : null}
                </div>
              </div>
              {touched.email && errors.email && (
                <span className="text-xs text-red-400 mt-1">
                  {errors.email}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              Una vez te sumes podrás unirte a un grupo de Telegram donde te
              mantendremos al tanto de las novedades antes del inicio de la Open
              Week.
            </p>

            <span className="block w-full rounded-md shadow-sm">
              <button
                type="submit"
                disabled={!isValid}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 focus:border-indigo-600 disabled:opacity-50 focus:shadow-outline-indigo transition duration-150 ease-in-out"
              >
                Continuar
              </button>
            </span>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BasicForm;
